import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CoverageRing — the security stack as a wheel, so the OPENINGS are the figure.
 *
 * The heatmap answers "who sells what here" and is the right tool once you
 * have room for eighteen vendor rows. It cannot answer the question a rep
 * actually opens the page with — which categories is nobody covering — because
 * an empty column in a matrix reads as background.
 *
 * So: one segment per product category, all EQUAL in angle. Deliberately not
 * weighted by spend. Weighting would shrink the categories with little or no
 * money in them to slivers, and those are precisely the ones worth selling
 * into; a coverage question is a count, not an amount.
 *
 * REBUILT after Ryan's review, which was right on all three counts:
 *
 *   "I'm not sure what this is really telling us."
 *     It was telling us something false. Coverage was `spend > 0`, so at DGS a
 *     $5 SOAR line and a $513 WAF line counted as covered exactly like
 *     $1.28M of Endpoint. The centre read "1 GAP" at an account with four
 *     categories nobody has meaningfully deployed. A toehold is not coverage,
 *     so there are now FOUR states and the fourth — `thin` — is the one that
 *     was hiding. The centre counts what is OPEN (thin + gap), because that is
 *     the number a rep can act on; "1" was arithmetically true and useless.
 *
 *   "There really are not a lot of relevant labels on the pieces of PIE."
 *     There were none: three <text> elements, all in the middle. Every segment
 *     now carries its own label, which is why the box grew and the short names
 *     in `securityCategories` exist.
 *
 *   "I can't drill down in any meaningful way."
 *     Every segment is clickable now, gaps INCLUDED — they were the one thing
 *     that was not, which is the exact inversion of what a widget named for
 *     gaps should do. What a click opens is the host's business; this chart's
 *     job is to make sure there is always something behind one.
 *
 * Segments are SORTED by state rather than left in catalog order, so the four
 * states form four contiguous arcs. Interleaved, thirteen little wedges are a
 * pattern; grouped, the open arc's size is the reading, at a glance, from
 * across a desk — which is the whole claim of drawing this as a wheel.
 *
 * Presentational: data via props, no fetching, colours from CSS variables.
 */
import { useMemo } from 'react';
import { securityCategoryShort } from './securityCategories.js';
const STATE_FILL = {
    ours: 'var(--accent, var(--color-primary, #3b82f6))',
    held: 'var(--color-text-muted, var(--text-muted, #6b7280))',
    thin: 'var(--color-text-muted, var(--text-muted, #9ca3af))',
    // Amber, not red. An uncovered category is an OPENING, not an error, and the
    // colour a rep sees first should not say "something is broken" about the one
    // piece of good news on the card.
    gap: 'var(--warn, #d97706)',
};
const STATE_OPACITY = {
    ours: 1,
    held: 0.62,
    thin: 0.22,
    gap: 0.1,
};
/** Plain-language names, so a legend never has to invent its own wording. */
export const COVERAGE_STATE_LABEL = {
    ours: 'Ours',
    held: 'Competitor-held',
    thin: 'Token spend only',
    gap: 'Nothing recorded',
};
/**
 * Below this, a category's spend is a line item rather than a deployment.
 *
 * Two parts, because one alone breaks at one end of the range. A pure
 * percentage of the account's own security spend calls a $600 line "real" at a
 * small agency; a pure dollar floor calls a genuine $30K deployment at a small
 * agency "token" while letting a $40K rounding error at a huge one pass. So:
 * 1% of what this account spends on security, and never less than $5,000.
 *
 * The number is stated on the widget's face. A threshold that changes what a
 * chart claims and is not written down anywhere is how "1 GAP" got believed.
 */
export const MATERIALITY_SHARE = 0.01;
export const MATERIALITY_MIN = 5000;
export function materialityFloor(categories) {
    const total = (categories || []).reduce((sum, c) => sum + (c.spend > 0 ? c.spend : 0), 0);
    return Math.max(total * MATERIALITY_SHARE, MATERIALITY_MIN);
}
/**
 * Which of the four a category is in.
 *
 * `floor` is optional so every existing caller keeps working; without it the
 * old binary answer comes back, which is wrong but never worse than it was.
 * Callers that want the truth pass `materialityFloor(categories)`.
 */
export function coverageState(c, floor = 0) {
    if (!(c.spend > 0))
        return 'gap';
    if (c.spend < floor)
        return 'thin';
    return c.ours ? 'ours' : 'held';
}
/** True for the states a rep can sell into. */
export function isOpen(state) {
    return state === 'gap' || state === 'thin';
}
/** The order the arcs are laid out in. Within a state, biggest first. */
const STATE_ORDER = ['ours', 'held', 'thin', 'gap'];
/** One donut segment. Angles in radians, 0 at 12 o'clock. */
function arcPath(cx, cy, rOuter, rInner, a0, a1) {
    const p = (r, a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
    const [x0o, y0o] = p(rOuter, a0);
    const [x1o, y1o] = p(rOuter, a1);
    const [x1i, y1i] = p(rInner, a1);
    const [x0i, y0i] = p(rInner, a0);
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return [
        `M ${x0o.toFixed(2)} ${y0o.toFixed(2)}`,
        `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1o.toFixed(2)} ${y1o.toFixed(2)}`,
        `L ${x1i.toFixed(2)} ${y1i.toFixed(2)}`,
        `A ${rInner} ${rInner} 0 ${large} 0 ${x0i.toFixed(2)} ${y0i.toFixed(2)}`,
        'Z',
    ].join(' ');
}
export const CoverageRing = ({ categories, selectedId, onPick, size = 190, fill = false, labels = true, }) => {
    const floor = useMemo(() => materialityFloor(categories || []), [categories]);
    const segments = useMemo(() => {
        const list = categories || [];
        const n = list.length;
        if (n === 0)
            return [];
        const withState = list.map((c) => ({ category: c, state: coverageState(c, floor) }));
        withState.sort((a, b) => {
            const byState = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
            if (byState !== 0)
                return byState;
            if (b.category.spend !== a.category.spend)
                return b.category.spend - a.category.spend;
            return a.category.label.localeCompare(b.category.label);
        });
        // A hairline between segments, so thirteen of them read as thirteen
        // answers rather than one continuous ring.
        const gapAngle = Math.min(((2 * Math.PI) / n) * 0.12, 0.05);
        const step = (2 * Math.PI) / n;
        return withState.map((s, i) => ({
            ...s,
            a0: i * step + gapAngle / 2,
            a1: (i + 1) * step - gapAngle / 2,
            mid: (i + 0.5) * step,
        }));
    }, [categories, floor]);
    if (segments.length === 0)
        return null;
    const open = segments.filter((s) => isOpen(s.state)).length;
    /*
     * The box grew from 200 to 320 to make room for labels OUTSIDE the ring.
     * Inside is not an option at 13 segments: a 27° wedge is 30px of chord at
     * this radius and "Vuln/Exposure Mgmt" is not going in it.
     *
     * 320 was still 24 units short for the LONGEST name. A left-anchored label
     * starts at `cx - rLabel` = 46 and "Observability" runs about 70 wide, so it
     * hung off the left edge and rendered as "servability" — measured in the
     * browser, not guessed. The margin has to clear the widest label the map can
     * produce, so the box is sized FROM that rather than picked: `rLabel` plus
     * room for the word, doubled.
     *
     * The wheel keeps its own radii, so this trades a slightly smaller wheel for
     * a legible name. That is the right way round — an unreadable label is worth
     * less than the pixels it costs.
     */
    const LONGEST_LABEL_UNITS = 72;
    const box = 2 * (104 + 10 + LONGEST_LABEL_UNITS);
    const cx = box / 2;
    const cy = box / 2;
    const rOuter = 104;
    const rInner = 70;
    const rLabel = rOuter + 10;
    return (_jsxs("svg", { viewBox: `0 0 ${box} ${box}`, width: fill ? '100%' : size, height: fill ? '100%' : size, role: "img", "aria-label": `Security coverage: ${open} of ${segments.length} categories open — nothing recorded or token spend only`, className: `chsvg${fill ? ' chsvg--fill' : ''}`, preserveAspectRatio: "xMidYMid meet", children: [segments.map(({ category, state, a0, a1, mid }) => {
                const selected = selectedId === category.id;
                const interactive = !!onPick;
                return (_jsxs("g", { children: [_jsx("path", { d: arcPath(cx, cy, selected ? rOuter + 5 : rOuter, rInner, a0, a1), fill: STATE_FILL[state], fillOpacity: selected ? Math.min(STATE_OPACITY[state] + 0.3, 1) : STATE_OPACITY[state], 
                            // An open category has no fill worth speaking of, so its OUTLINE
                            // is what makes it legible — otherwise "nothing here" and
                            // "background" look alike, which is the exact failure this chart
                            // exists to fix.
                            stroke: isOpen(state) ? STATE_FILL.gap : 'var(--color-surface, var(--surface, #fff))', strokeWidth: isOpen(state) ? 1.5 : 1, strokeDasharray: state === 'gap' ? '4 3' : undefined, className: interactive ? 'chpick' : undefined, 
                            // EVERY segment answers a click, gaps included. A gap has no
                            // transactions, which is a fact about the account and not a
                            // reason to make the segment inert: what it opens is an
                            // explanation and the statewide market, which is the more useful
                            // of the two drills.
                            onClick: interactive ? () => onPick(category) : undefined, tabIndex: interactive ? 0 : undefined, onKeyDown: interactive ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onPick(category);
                                }
                            } : undefined, role: interactive ? 'button' : undefined, "aria-label": `${category.label}: ${state === 'gap' ? 'nothing recorded'
                                : state === 'thin' ? `token spend only — ${category.topVendor || 'one line'}`
                                    : state === 'ours' ? `ours — ${category.topVendor || 'Palo Alto'}`
                                        : `held by ${category.topVendor || 'a competitor'}`}` }), labels && (_jsx("text", { x: cx + rLabel * Math.sin(mid), y: cy - rLabel * Math.cos(mid), 
                            // Anchored by which side of the circle it is on, so nothing
                            // overhangs the box and the ring stays centred.
                            textAnchor: Math.sin(mid) > 0.08 ? 'start' : Math.sin(mid) < -0.08 ? 'end' : 'middle', dominantBaseline: Math.cos(mid) > 0.6 ? 'auto' : Math.cos(mid) < -0.6 ? 'hanging' : 'middle', fontSize: 10, fontWeight: selected ? 700 : 500, fill: selected || isOpen(state)
                                ? 'var(--color-text, var(--text, #111827))'
                                : 'var(--color-text-muted, var(--text-muted, #6b7280))', 
                            // Pointer events off: the label sits outside its own wedge, so
                            // a click on it would otherwise land on nothing and read as a
                            // dead spot on the chart.
                            className: "chlabel", children: securityCategoryShort(category.label) }))] }, category.id));
            }), _jsx("text", { x: cx, y: cy - 4, textAnchor: "middle", fontSize: 34, fontWeight: 700, fill: "var(--color-text, var(--text, #111827))", className: "chnum", children: open }), _jsx("text", { x: cx, y: cy + 15, textAnchor: "middle", fontSize: 10, letterSpacing: "0.08em", fill: "var(--color-text-muted, var(--text-muted, #9ca3af))", children: "OPEN TO US" }), _jsxs("text", { x: cx, y: cy + 30, textAnchor: "middle", fontSize: 9, fill: "var(--color-text-muted, var(--text-muted, #9ca3af))", children: ["of ", segments.length, " categories"] })] }));
};
export default CoverageRing;
